/**
 * CAOS-290 — the read seam over the cross-publisher registry, for the in-shell exchange surface.
 *
 * ── WHY THIS IS A SEAM AND NOT A FETCH INSIDE THE SURFACE ────────────────────────────────────
 * The same shape as {@link import('./install-client.js').InstallClient}, and for the same reason:
 * the surface is driven by deterministic component tests and by a dev fixture with no kernel behind
 * it, and a `fetch` written inline would make both of those a network stub rather than a boundary.
 * The default implementation GETs the real route; {@link setExchangeClient} replaces it.
 *
 * ── THREE OUTCOMES, AND THE MIDDLE ONE IS WHY THIS IS NOT `Promise<ExchangePage>` ────────────
 * A browse surface has to tell these apart, and two of them look identical to a caller that only
 * sees a list:
 *
 *   1. read, and the registry holds versions — a page of them;
 *   2. read, and the registry holds NOTHING — `entries: []`, `total: 0`. Nothing has been published
 *      yet. A real, correct, renderable answer;
 *   3. NOT read — no permission, a kernel too old to answer the route, an unreachable platform, a
 *      body that is not a page. `ok: false` with a reason.
 *
 * Collapsing 3 into 2 is the specific failure this shape exists to prevent: the route is gated on
 * `setup.access`, so the commonest reason a reader gets nothing is that they are not an
 * administrator — and answering that with "nothing has been published" would be a confident false
 * statement about the whole marketplace, made to the person least able to check it. The same
 * distinction `readInstalledPackages` draws in `surfaces/setup-surface.ts`, for the same reason.
 *
 * ── FAIL-SOFT, NEVER THROWN ──────────────────────────────────────────────────────────────────
 * Every failure becomes `ok: false` carrying a legible sentence and, when the platform gave one,
 * its code. The caller is a render path: a screen that cannot say what is published should say so,
 * never fail to draw.
 */

/** The path the default client GETs (proxied to the Engine's `packages/published`, CAOS-1682). */
export const PACKAGES_PUBLISHED_PATH = '/api/packages/published';

/**
 * The page size the surface asks for — the route's own documented ceiling.
 *
 * `PUBLISHED_CATALOG_MAX_LIMIT` in `kernel/src/packaging/published-catalog.ts` is 200 and the route
 * enforces it rather than trusting a caller, so asking for more would be silently reduced. Asking
 * for exactly it is what makes the one-page case the common case, and that matters to the surface
 * rather than only to the transport: a filter applied to a page that IS the whole catalogue is a
 * catalogue-wide filter, and applied to one page of several it is not. See {@link isWholeCatalogue}.
 */
export const EXCHANGE_PAGE_SIZE = 200;

/** One kind of member a published version contains, with how many and which keys. */
export interface ExchangeMemberGroup {
  readonly kind: string;
  readonly count: number;
  readonly keys: readonly string[];
}

/**
 * What a version contains, as the registry describes it.
 *
 * `totalMembers` is the true length of the stored member array and `groups` covers only the members
 * the registry could describe, so the two are ALLOWED to disagree — the kernel's
 * `PublishedCatalogMemberSummary` says so explicitly. The surface reads the difference and says it
 * out loud rather than rendering the groups as though they were the whole contents.
 */
export interface ExchangeMemberSummary {
  readonly totalMembers: number;
  readonly groups: readonly ExchangeMemberGroup[];
}

/** One dependency a published version declares. */
export interface ExchangeDependency {
  readonly package: string;
  readonly range: string;
}

/**
 * One published version, as the in-shell exchange surface sees it.
 *
 * Read off the wire as a wire shape rather than imported from the kernel, which is what every other
 * host boundary here does (`Prerequisite` in `install-client.ts` records the same choice).
 *
 * THERE IS NO DISPLAY NAME ON THIS SHAPE, and that is the registry's state rather than an omission
 * here — `caos.published_version` stores no label. `exchangeListingLabel` in `exchange-surface.ts`
 * is where the surface decides what to call a package, and records why it does not invent one.
 */
export interface ExchangeListing {
  /** The install credential. What {@link import('./install-client.js').InstallClient.installByKey} takes. */
  readonly universalKey: string;
  readonly packageKey: string;
  readonly version: string;
  readonly namespace: string | null;
  /** When the version was published. */
  readonly createdAt: string;
  readonly publisherOrgId: string;
  /** Null means the publishing org has no publisher profile — "not registered", not "no name". */
  readonly companyName: string | null;
  readonly dependencies: readonly ExchangeDependency[];
  /** `protected` means installing ALSO needs a secret key. It gates the install, not the listing. */
  readonly protection: 'public' | 'protected';
  /** Null means BETA — installable by key only into a trial or sandbox org. */
  readonly promotedAt: string | null;
  /** Null when the contents could not be read — a different fact from containing nothing. */
  readonly memberSummary: ExchangeMemberSummary | null;
}

/** A page of the cross-publisher catalogue, and enough to know whether it is the whole of it. */
export interface ExchangePage {
  readonly listings: readonly ExchangeListing[];
  /** Live versions matching the read, ignoring `limit`/`offset`. */
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
  /**
   * Rows the platform sent on this page that this reader could not read, and so dropped.
   *
   * CARRIED RATHER THAN SWALLOWED, because a review found what swallowing it cost. `listings` is
   * what could be read and `total` is the registry's own count, so when the two disagree the
   * difference has two completely different causes — this page is a window onto a bigger
   * catalogue, or some of its rows were unreadable — and a surface that cannot tell them apart
   * says the wrong thing about both. A page whose every row was malformed came out as `listings:
   * []` with `total > 0`, which rendered as "Nothing here matches that filter" with no filter set,
   * and as "Showing 1–0 of 9 versions".
   *
   * It is the same distinction `memberSummary: null` draws one level down: "there is nothing" and
   * "it could not be read" are different statements, and this is the count that keeps them apart
   * at the page level.
   */
  readonly dropped: number;
}

/** The outcome of one read. `ok: false` carries a human-readable `error`. */
export interface ExchangeReadOutcome {
  readonly ok: boolean;
  readonly page?: ExchangePage;
  readonly error?: string;
  readonly code?: string;
}

/** What the surface asks for. Mirrors the route's own three parameters. */
export interface ExchangeReadOptions {
  readonly limit?: number;
  readonly offset?: number;
  /** Narrow to one package key — the route's only filter, and an EXACT match. */
  readonly packageKey?: string;
}

/** The read boundary the exchange surface drives. */
export interface ExchangeClient {
  published(opts?: ExchangeReadOptions): Promise<ExchangeReadOutcome>;
}

/**
 * Whether this page's WINDOW spans the entire catalogue, rather than being one view onto it.
 *
 * The surface turns on this. A text filter over the whole catalogue is a catalogue-wide filter and
 * may say so; over one page of several it narrows only what is on screen, and claiming otherwise
 * would tell somebody an app is not published when it is simply on page two.
 *
 * ── IT ASKS ABOUT THE WINDOW, NOT ABOUT HOW MANY ROWS PARSED ────────────────────────────────
 * This was `listings.length === total`, borrowed from `reconcileExchangeCatalog`'s precondition in
 * the kernel. That is the right test THERE and the wrong one here, and a review is what made the
 * difference visible. The kernel's question is "do I hold every entry?", because it reads an
 * absence as a revocation. The surface's question is "does one window cover the catalogue?", and a
 * row this reader had to drop (see {@link ExchangePage.dropped}) must not turn a complete read into
 * an apparently partial one — `total: 4` with one unreadable row would otherwise claim three more
 * pages exist and strand a reader on an empty one.
 *
 * `offset === 0` is part of it rather than implied: a second page of a two-page catalogue satisfies
 * the arithmetic below on its own, and it is emphatically not the whole of anything.
 */
export function isWholeCatalogue(page: ExchangePage): boolean {
  // A page that echoed no usable `limit` can only speak for the rows it actually carried.
  const window = page.limit > 0 ? page.limit : page.listings.length;
  return page.offset === 0 && page.offset + window >= page.total;
}

/** The substrate passthrough envelope, as read off the wire. */
type PublishedEnvelope =
  | { ok: true; data?: unknown }
  | { ok: false; error?: { message?: string; code?: string } };

/**
 * The default HTTP client: GET {@link PACKAGES_PUBLISHED_PATH}, read the substrate
 * `{ ok, data | error }` envelope.
 *
 * `credentials: 'same-origin'` because the route is authenticated — `host/src/worker.ts` re-injects
 * the session bearer, exactly as it does for the install doors.
 */
export const httpExchangeClient: ExchangeClient = {
  async published(opts: ExchangeReadOptions = {}): Promise<ExchangeReadOutcome> {
    const query = new URLSearchParams();
    if (opts.limit !== undefined) query.set('limit', String(opts.limit));
    if (opts.offset !== undefined) query.set('offset', String(opts.offset));
    if (opts.packageKey !== undefined) query.set('packageKey', opts.packageKey);
    // `toString()` rather than `query.size`, which is newer than the browsers and the test
    // environment this has to run in, and would be an empty-query bug only off the happy path.
    const search = query.toString();
    const path = search === '' ? PACKAGES_PUBLISHED_PATH : `${PACKAGES_PUBLISHED_PATH}?${search}`;

    let response: Response;
    try {
      response = await fetch(path, {
        method: 'GET',
        headers: { accept: 'application/json' },
        credentials: 'same-origin',
      });
    } catch (err) {
      return {
        ok: false,
        code: 'exchange.unreachable',
        error: `Could not reach the platform to read what has been published (${
          err instanceof Error ? err.message : String(err)
        }).`,
      };
    }

    let envelope: PublishedEnvelope;
    try {
      envelope = (await response.json()) as PublishedEnvelope;
    } catch {
      return {
        ok: false,
        code: 'exchange.bad_response',
        error: `The platform returned a non-JSON response (HTTP ${response.status}).`,
      };
    }

    if (!envelope || envelope.ok !== true) {
      const detail =
        envelope && envelope.ok === false && envelope.error?.message
          ? envelope.error.message
          : `HTTP ${response.status}`;
      const code = envelope && envelope.ok === false ? envelope.error?.code : undefined;
      return { ok: false, ...(code === undefined ? {} : { code }), error: `Could not read what has been published (${detail}).` };
    }

    const page = readPage(envelope.data);
    if (page === null) {
      return {
        ok: false,
        code: 'exchange.bad_page',
        // Not an empty catalogue. A body this reader cannot recognise as a page is a read that
        // failed, and reporting it as "nothing is published" would be the same false claim the
        // module docblock exists to refuse.
        error: 'The platform answered with something that is not a catalogue page.',
      };
    }
    return { ok: true, page };
  },
};

/**
 * Read a page off the wire, or `null` when the body is not one.
 *
 * Validated rather than cast. The route is the only producer today, but a cast would let a 200 from
 * anything at all — a sign-in page, a proxy error document with a JSON body — render as an empty
 * marketplace, and the whole point of the three-outcome shape is that an empty marketplace is a
 * claim.
 *
 * A single malformed ENTRY is dropped rather than failing the page, which is the same bargain the
 * kernel's own read makes one level down (a malformed artifact answers `memberSummary: null` so one
 * bad row does not fail a read across every publisher). A page that lists nine readable versions
 * and one unreadable row is far more useful than no page at all.
 */
function readPage(data: unknown): ExchangePage | null {
  if (data === null || typeof data !== 'object') return null;
  const body = data as Record<string, unknown>;
  if (!Array.isArray(body.entries)) return null;
  if (typeof body.total !== 'number' || !Number.isFinite(body.total)) return null;

  const listings = body.entries.map(readListing).filter((l): l is ExchangeListing => l !== null);
  return {
    listings,
    total: body.total,
    // `limit` and `offset` are echoed by the route, but a page that omitted them is still a page:
    // fall back to what was in fact applied rather than refusing the whole read over an echo.
    limit: typeof body.limit === 'number' && Number.isFinite(body.limit) ? body.limit : listings.length,
    offset: typeof body.offset === 'number' && Number.isFinite(body.offset) ? body.offset : 0,
    // What the platform sent minus what could be read. See the field's own note for why this is
    // carried out rather than quietly absorbed into a short list.
    dropped: body.entries.length - listings.length,
  };
}

function readListing(value: unknown): ExchangeListing | null {
  if (value === null || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  // The four fields without which a row cannot be rendered OR installed. Everything else below has
  // a defined reading when absent; these do not.
  if (typeof row.universalKey !== 'string' || row.universalKey === '') return null;
  if (typeof row.packageKey !== 'string' || row.packageKey === '') return null;
  if (typeof row.version !== 'string' || row.version === '') return null;
  if (typeof row.publisherOrgId !== 'string') return null;

  return {
    universalKey: row.universalKey,
    packageKey: row.packageKey,
    version: row.version,
    namespace: typeof row.namespace === 'string' ? row.namespace : null,
    createdAt: typeof row.createdAt === 'string' ? row.createdAt : '',
    publisherOrgId: row.publisherOrgId,
    companyName: typeof row.companyName === 'string' ? row.companyName : null,
    dependencies: readDependencies(row.dependencies),
    // Anything that is not the string `protected` reads as `public`, matching the kernel's own
    // mapping. A protection this reader did not recognise must not become the stricter claim: the
    // surface withholds the install button for `protected`, so guessing it would hide an
    // installable package behind a key requirement that does not exist.
    protection: row.protection === 'protected' ? 'protected' : 'public',
    promotedAt: typeof row.promotedAt === 'string' ? row.promotedAt : null,
    memberSummary: readMemberSummary(row.memberSummary),
  };
}

function readDependencies(value: unknown): readonly ExchangeDependency[] {
  if (!Array.isArray(value)) return [];
  const out: ExchangeDependency[] = [];
  for (const d of value) {
    if (d === null || typeof d !== 'object') continue;
    const dep = d as Record<string, unknown>;
    if (typeof dep.package !== 'string' || dep.package === '') continue;
    out.push({ package: dep.package, range: typeof dep.range === 'string' ? dep.range : '' });
  }
  return out;
}

/**
 * The contents summary, or `null` when the registry could not describe them.
 *
 * Null is carried through rather than flattened to an empty summary, because the kernel went to
 * deliberate trouble to distinguish the two and the surface says different sentences for each:
 * "contains nothing" and "contents could not be read" are different statements about a package.
 */
function readMemberSummary(value: unknown): ExchangeMemberSummary | null {
  if (value === null || typeof value !== 'object') return null;
  const summary = value as Record<string, unknown>;
  const groups: ExchangeMemberGroup[] = [];
  if (Array.isArray(summary.groups)) {
    for (const g of summary.groups) {
      if (g === null || typeof g !== 'object') continue;
      const group = g as Record<string, unknown>;
      if (typeof group.kind !== 'string' || group.kind === '') continue;
      groups.push({
        kind: group.kind,
        count: typeof group.count === 'number' && Number.isFinite(group.count) ? group.count : 0,
        keys: Array.isArray(group.keys) ? group.keys.filter((k): k is string => typeof k === 'string') : [],
      });
    }
  }
  return {
    totalMembers:
      typeof summary.totalMembers === 'number' && Number.isFinite(summary.totalMembers)
        ? summary.totalMembers
        : groups.reduce((n, g) => n + g.count, 0),
    groups,
  };
}

let client: ExchangeClient = httpExchangeClient;

/** Override the read boundary (dev fixture / tests). */
export function setExchangeClient(next: ExchangeClient): void {
  client = next;
}

/** The active read boundary — the surface's default when no client is injected. */
export function getExchangeClient(): ExchangeClient {
  return client;
}
